import React from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { Timezones } from "hooks/useTimezone";
import { DweenTimeUnit, fmtDatetime, toZonedDateTime } from "utils/time-utils";
import { ChronoUnit, ZonedDateTime } from "@js-joda/core";
import { TimeOptions } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { DATE_FORMATTER_HOURS_MINUTES_ONLY, DATE_FORMATTER_MONTH_DAY_ONLY } from "utils/constants";
import { WeatherMonitoringProps } from "../charts/interfaces";
import {
  getAverageWindDirection,
  getCurrentHourParameter,
  getMaxParameter,
  getMinMaxWindSpeed,
} from "./WeatherMonitoringUtils";

const createColumnBody = (
  datasetsData: DatasetDataInfoResponse[],
  props: WeatherMonitoringProps,
  currentStartTime: ZonedDateTime,
  currentEndTime: ZonedDateTime,
  timeInterval: string,
  tz: Timezones
) => {
  const temperatureDataArr = datasetsData?.find(
    d => d.identifier === props.temperature?.dataset?.identifier
  )?.data;
  const humidityDataArr = datasetsData?.find(d => d.identifier === props.humidity?.dataset?.identifier)?.data;
  const precipitationDataArr = datasetsData?.find(
    d => d.identifier === props.precipitation?.dataset?.identifier
  )?.data;
  const windSpeedDataArr = datasetsData?.find(
    d => d.identifier === props.windSpeed?.dataset?.identifier
  )?.data;
  const windDirectionDataArr = datasetsData?.find(
    d => d.identifier === props.windDirection?.dataset?.identifier
  )?.data;

  const maxTemperature = getMaxParameter(
    temperatureDataArr || [],
    currentStartTime,
    currentEndTime,
    "Temperature",
    tz
  );
  const { min: minWindSpeed, max: maxWindSpeed } = getMinMaxWindSpeed(
    windSpeedDataArr || [],
    currentStartTime,
    currentEndTime,
    tz
  );
  const avgWindDirection = getAverageWindDirection(
    windDirectionDataArr || [],
    currentStartTime,
    currentEndTime,
    tz
  );

  const maxHumidity = getMaxParameter(
    humidityDataArr || [],
    currentStartTime,
    currentEndTime,
    "Humidity",
    tz
  );
  const maxPrecipitation = getMaxParameter(
    precipitationDataArr || [],
    currentStartTime,
    currentEndTime,
    "Precipitation",
    tz
  );

  return (
    <div className="flex flex-column gap-2 font-semibold">
      <div style={{ color: `${props.temperature?.dataset?.line?.color}` }}>
        {timeInterval === TimeOptions.DAYS.name
          ? maxTemperature
          : getCurrentHourParameter(temperatureDataArr || [], currentStartTime, tz)}{" "}
        °C
      </div>
      <div style={{ color: `${props.humidity?.dataset?.line?.color}` }}>
        {timeInterval === TimeOptions.DAYS.name
          ? maxHumidity
          : getCurrentHourParameter(humidityDataArr || [], currentStartTime, tz)}
        %
      </div>
      <div style={{ color: `${props.precipitation?.dataset?.line?.color}` }}>
        {timeInterval === TimeOptions.DAYS.name
          ? maxPrecipitation
          : getCurrentHourParameter(precipitationDataArr || [], currentStartTime, tz)}{" "}
        mm
      </div>
      {timeInterval === TimeOptions.DAYS.name ? (
        <div style={{ color: `${props.windSpeed?.dataset?.line?.color}` }}>
          {minWindSpeed} - {maxWindSpeed} m/s
        </div>
      ) : (
        <div style={{ color: `${props.windSpeed?.dataset?.line?.color}` }}>
          {getCurrentHourParameter(windSpeedDataArr || [], currentStartTime, tz)} m/s
        </div>
      )}

      <Button
        icon="pi pi-arrow-up"
        disabled
        className="p-button-text p-button-secondary p-0"
        style={{ transform: `rotate(${avgWindDirection}deg)` }}
      />
    </div>
  );
};

const generateColumns = (
  datasetsData: DatasetDataInfoResponse[],
  props: WeatherMonitoringProps,
  timeInterval: DweenTimeUnit,
  startTime: Date,
  tz: Timezones
) => {
  const totalIntervals = timeInterval === TimeOptions.HOURS.name ? 48 : 7;

  let zonedStartTime = toZonedDateTime(startTime, tz.company);

  if (timeInterval === TimeOptions.HOURS.name) {
    zonedStartTime = zonedStartTime.truncatedTo(ChronoUnit.HOURS);
  } else if (timeInterval === TimeOptions.DAYS.name) {
    zonedStartTime = zonedStartTime.truncatedTo(ChronoUnit.DAYS);
  }

  let initialStart = zonedStartTime;

  return Array.from({ length: totalIntervals }, (_, i) => {
    const startDate = initialStart;
    let endDate: ZonedDateTime;

    if (timeInterval === TimeOptions.DAYS.name) {
      endDate = startDate.plusDays(1).truncatedTo(ChronoUnit.DAYS);
    } else {
      endDate = startDate.plusHours(1);
    }

    initialStart = endDate;

    const headerFormat =
      timeInterval === TimeOptions.DAYS.name
        ? DATE_FORMATTER_MONTH_DAY_ONLY
        : DATE_FORMATTER_HOURS_MINUTES_ONLY;
    const headerLabel = fmtDatetime(startDate, tz.display, headerFormat);

    return (
      <Column
        key={i}
        field={`weatherIcon${i}`}
        style={{ width: timeInterval === TimeOptions.HOURS.name ? "5rem" : "20rem" }}
        header={headerLabel}
        body={() =>
          createColumnBody(
            datasetsData,
            props,
            startDate,
            timeInterval === TimeOptions.DAYS.name ? endDate.plusDays(1).minusNanos(1) : endDate,
            timeInterval,
            tz
          )
        }
      />
    );
  });
};

export default generateColumns;
